import { BaseDocument, BaseDocumentJSON } from './BaseDocuments'
import { Highlight, COORDINATE_KEYS, Coordinates } from './Documents'

type Point = {
  x: number
  y: number
}

type BoundingPoly = {
  vertices: Point[]
}

type Field = {
  boundingPoly: BoundingPoly
  inferConfidence: number
  inferText: string
  lineBreak: boolean
  type: string
  valueType: string
}

type CellWord = {
  boundingPoly: BoundingPoly
  inferConfidence: number
  inferText: string
}

type CellTextLine = {
  boundingPoly: BoundingPoly
  cellWords: CellWord[]
  inferConfidence: number
}

type Cell = {
  boundingPoly: BoundingPoly
  cellTextLines: CellTextLine[]
  columnIndex: number
  columnSpan: number
  rowIndex: number
  rowSpan: number
  inferConfidence: number
}

type Table = {
  boundingPoly: BoundingPoly
  cells: Cell[]
}

type ValidationResult = {
  result: string
}

type ConvertedImageInfo = {
  height: number
  pageIndex: number
  width: number
}

type OcrRespGeneral = {
  convertedImageInfo: ConvertedImageInfo
  fields: Field[]
  tables: Table[]
  validationResult: ValidationResult
  inferResult: string
  message: string
  name: string
  uid: string
}

type OcrEditItem = {
  no: number
  text: string
  type: string
  valueType: string
}

type OcrEditCellTextLine = {
  cellWords: { text: string }[]
}

type OcrEditCell = {
  cIdx: number | undefined // not from api result. filled by component
  cellTextLines: OcrEditCellTextLine[]
  editedText: string
  columnIndex: number
  columnSpan: number
  rowIndex: number
  rowSpan: number
}

type OcrEditTable = {
  cells: OcrEditCell[]
}

type OcrEditResultGeneral = {
  editedText: string
  fields: OcrEditItem[]
  tables: OcrEditTable[]
}

export type DocumentGeneralJSON = BaseDocumentJSON & {
  ocrResp: OcrRespGeneral
  ocrEditResult: OcrEditResultGeneral
}

export class DocumentGeneral extends BaseDocument {
  // re-arrange cells by table/row/column
  tablesByRowCol: OcrEditCell[][][] = []
  maxCols: number[] = []
  constructor(public docJSON: DocumentGeneralJSON) {
    super()
    const mcs: number[] = []
    const tss: OcrEditCell[][][] = []
    this.ocrEditResult.tables?.forEach((t) => {
      const [maxRowIndex, maxColIndex] = this.findMaxIndice(t)
      const table: OcrEditCell[][] = []
      for (let i = 0; i <= maxRowIndex; i++) table.push([])
      t.cells.forEach((c) => {
        table[c.rowIndex].push(c)
      })
      mcs.push(maxColIndex + 1)
      tss.push(table)
    })
    tss.forEach((t) => {
      let cIdx = 0
      t.forEach((r) => {
        r.sort((a, b) => a.columnIndex - b.columnIndex)
        r.forEach((c) => (c.cIdx = cIdx++))
      })
    })

    this.tablesByRowCol = tss
    this.maxCols = mcs
  }

  findMaxIndice(t: DocumentGeneralJSON['ocrEditResult']['tables'][0]) {
    let [maxRow, maxColumn] = [0, 0]
    t.cells.forEach((c) => {
      maxRow = c.rowIndex > maxRow ? c.rowIndex : maxRow
      maxColumn = c.columnIndex > maxColumn ? c.columnIndex : maxColumn
    })
    return [maxRow, maxColumn]
  }

  get isGeneral() {
    return true
  }
  get ocrResp() {
    return this.docJSON.ocrResp
  }
  get ocrEditResult() {
    return this.docJSON.ocrEditResult
  }
  get config() {
    return undefined
  }

  verify(_edit: { [x: string]: any }) {
    throw Error('Not implemented')
  }

  getHighlights() {
    return getHighlights(this.docJSON)
  }

  async save(edit: Parameters<BaseDocument['save']>[0]) {
    // cells are edited on the document state directly
    // only text area input is registed as react hook form
    return super.save({ ...this.ocrEditResult, editedText: edit.editedText })
  }
}

const boundingPolyToCoordinates = (
  boundingPoly: BoundingPoly,
): Coordinates | null => {
  if (!boundingPoly?.vertices) {
    return null
  }

  return boundingPoly.vertices.reduce((result, vertex, idx) => {
    const key = COORDINATE_KEYS[idx]
    result[key] = [vertex.x, vertex.y]
    return result
  }, {} as Coordinates)
}

export function getHighlights({ ocrResp }: DocumentGeneralJSON): Highlight[] {
  const highlights: Highlight[] = []

  ocrResp.fields.forEach((item, idx) => {
    const coordinates = boundingPolyToCoordinates(item.boundingPoly)
    if (!coordinates) {
      return
    }

    highlights.push({
      name: `field.${idx}`,
      label: '',
      detectedPage: 0,
      coordinates,
    })
  })

  ocrResp.tables?.forEach((t, tIndex) => {
    t.cells.forEach((c) => {
      const coordinates = boundingPolyToCoordinates(c.boundingPoly)
      if (!coordinates) {
        return
      }

      highlights.push({
        name: `cell.${tIndex}.${c.rowIndex}.${c.columnIndex}`,
        label: '',
        detectedPage: 0,
        coordinates,
      })
    })
  })

  return highlights
}
